import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PAGE_META, usePageMeta } from '../lib/pageMeta';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  PlusIcon,
  PencilIcon,
  EyeIcon,
  ArrowLeftIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  TrashIcon,
  AcademicCapIcon,
  WifiIcon,
  BuildingOfficeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import TrainerSearchSelect from './ui/TrainerSearchSelect';

// ─── Config ───────────────────────────────────────────────────────────────────
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const getToken = () => localStorage.getItem('admin_token');

// ─── Blank course form ────────────────────────────────────────────────────────
const BLANK_COURSE = {
  name: '',
  description: '',
  duration: '',
  durationUnit: 'months',
  fees: {
    online:  { totalAmount: '', currency: 'INR' },
    offline: { totalAmount: '', currency: 'INR' },
    weekend: { totalAmount: '', currency: 'INR' },
  },
};

// Auto-detect unit: ≤ 12 → months, > 12 → days
const autoUnit = (val) => (!isNaN(parseInt(val)) && parseInt(val) > 12 ? 'days' : 'months');

// Format for display: "6 months" or "45 days"
const fmtDuration = (duration, unit) => `${duration} ${unit || (duration > 12 ? 'days' : 'months')}`;

// ─── Shared error extractor ───────────────────────────────────────────────────
/**
 * Reads the JSON body of a failed response and returns the `error` string,
 * or a fallback message if the body can't be parsed or has no `error` field.
 */
const extractServerError = async (response, fallback) => {
  try {
    const body = await response.json();
    return body.error || fallback;
  } catch {
    return fallback;
  }
};

// ─── API service ──────────────────────────────────────────────────────────────
const apiService = {
  async getCourses(page = 1, limit = 10, filters = {}) {
    const params = new URLSearchParams({ page, limit, ...filters });
    const r = await fetch(`${API_BASE_URL}/course-dashboard/courses?${params}`, {
      headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
    });
    if (!r.ok) throw new Error(await extractServerError(r, 'Failed to fetch courses'));
    return r.json();
  },

  async getCourse(courseId) {
    const r = await fetch(`${API_BASE_URL}/course-dashboard/courses/${courseId}`, {
      headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
    });
    if (!r.ok) throw new Error(await extractServerError(r, 'Failed to fetch course'));
    return r.json();
  },

  async getBatch(batchId) {
    const r = await fetch(`${API_BASE_URL}/course-dashboard/batches/${batchId}`, {
      headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
    });
    if (!r.ok) throw new Error(await extractServerError(r, 'Failed to fetch batch'));
    return r.json();
  },

  async createCourse(courseData) {
    const r = await fetch(`${API_BASE_URL}/courses`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(courseData),
    });
    if (!r.ok) throw new Error(await extractServerError(r, 'Failed to create course'));
    return r.json();
  },

  async updateCourse(courseId, courseData) {
    const r = await fetch(`${API_BASE_URL}/courses/${courseId}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(courseData),
    });
    if (!r.ok) throw new Error(await extractServerError(r, 'Failed to update course'));
    return r.json();
  },

  async createBatch(batchData) {
    const r = await fetch(`${API_BASE_URL}/batches`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(batchData),
    });
    if (!r.ok) throw new Error(await extractServerError(r, 'Failed to create batch'));
    return r.json();
  },

  async updateBatch(batchId, batchData) {
    const r = await fetch(`${API_BASE_URL}/batches/${batchId}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(batchData),
    });
    if (!r.ok) throw new Error(await extractServerError(r, 'Failed to update batch'));
    return r.json();
  },

};

// ─── Skeleton components ──────────────────────────────────────────────────────
const SkeletonShimmer = ({ className = '', delay = 0 }) => (
  <div className={`relative overflow-hidden bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded-lg ${className}`} style={{ animationDelay: `${delay}ms` }}>
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
  </div>
);
const SkeletonPulse = ({ className = '', delay = 0, color = 'blue' }) => {
  const colors = { blue: 'from-blue-100 to-blue-200', violet: 'from-violet-100 to-violet-200', green: 'from-green-100 to-green-200', orange: 'from-orange-100 to-orange-200' };
  return <div className={`bg-gradient-to-br ${colors[color]} rounded-lg animate-[bubble-pulse_1.5s_ease-in-out_infinite] ${className}`} style={{ animationDelay: `${delay}ms` }} />;
};
const SkeletonWave = ({ className = '', delay = 0 }) => (
  <div className={`relative overflow-hidden bg-gray-200 rounded-lg ${className}`} style={{ animationDelay: `${delay}ms` }}>
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-200/50 to-transparent animate-[wave_1.8s_ease-in-out_infinite]" />
  </div>
);
const StatsCardSkeleton = ({ color = 'blue', delay = 0 }) => (
  <Card className="transform scale-95 animate-[scale-in_0.5s_ease-out_forwards]" style={{ animationDelay: `${delay}ms` }}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex-1 space-y-3">
          <SkeletonShimmer className="h-4 w-32" delay={delay} />
          <SkeletonShimmer className="h-8 w-20" delay={delay + 100} />
        </div>
        <SkeletonPulse className="h-12 w-12 rounded-lg" color={color} delay={delay + 200} />
      </div>
    </CardContent>
  </Card>
);
const TableRowSkeleton = ({ delay = 0 }) => (
  <TableRow className="animate-[fade-in_0.5s_ease-out_forwards] opacity-0" style={{ animationDelay: `${delay}ms` }}>
    <TableCell className="py-3">
      <div className="flex items-center space-x-3">
        <SkeletonPulse className="w-10 h-10 rounded-lg" color="blue" delay={delay} />
        <div className="flex-1 space-y-2">
          <SkeletonShimmer className="h-4 w-40" delay={delay + 50} />
          <SkeletonShimmer className="h-3 w-56" delay={delay + 100} />
        </div>
      </div>
    </TableCell>
    <TableCell><SkeletonShimmer className="h-4 w-16" delay={delay + 120} /></TableCell>
    <TableCell><SkeletonWave className="h-6 w-20 rounded-full" delay={delay + 140} /></TableCell>
    <TableCell>
      <div className="space-y-1">
        <SkeletonShimmer className="h-3 w-24" delay={delay + 160} />
        <SkeletonShimmer className="h-3 w-24" delay={delay + 180} />
      </div>
    </TableCell>
    <TableCell><SkeletonShimmer className="h-4 w-12" delay={delay + 200} /></TableCell>
    <TableCell><SkeletonShimmer className="h-4 w-12" delay={delay + 220} /></TableCell>
    <TableCell><SkeletonPulse className="h-6 w-16 rounded-full" color="green" delay={delay + 240} /></TableCell>
    <TableCell>
      <div className="flex space-x-1">
        <SkeletonPulse className="h-8 w-8 rounded" color="violet" delay={delay + 260} />
        <SkeletonPulse className="h-8 w-8 rounded" color="violet" delay={delay + 280} />
      </div>
    </TableCell>
  </TableRow>
);
const BatchCardSkeleton = ({ delay = 0 }) => (
  <Card className="border-2 animate-[scale-in_0.5s_ease-out_forwards] transform scale-95" style={{ animationDelay: `${delay}ms` }}>
    <CardContent className="p-4">
      <div className="flex flex-col lg:flex-row justify-between gap-4">
        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-3">
            <SkeletonShimmer className="h-6 w-48" delay={delay} />
            <SkeletonPulse className="h-6 w-20 rounded-full" color="green" delay={delay + 50} />
          </div>
          <div className="grid grid-cols-4 gap-3">
            {[0, 80, 160, 240].map((d, i) => (
              <div key={i} className="space-y-2">
                <SkeletonShimmer className="h-3 w-16" delay={delay + d} />
                <SkeletonShimmer className="h-4 w-28" delay={delay + d + 40} />
              </div>
            ))}
          </div>
        </div>
        <div className="flex lg:flex-col gap-2">
          <SkeletonPulse className="h-9 w-28 rounded" color="violet" delay={delay + 300} />
          <SkeletonPulse className="h-9 w-28 rounded" color="violet" delay={delay + 340} />
          <SkeletonPulse className="h-9 w-28 rounded" color="orange" delay={delay + 380} />
        </div>
      </div>
    </CardContent>
  </Card>
);
const BatchDetailsLoadingSkeleton = () => (
  <div className="space-y-4 animate-[fade-in_0.5s_ease-out]">
    {[0, 100, 200, 300, 400].map((delay, i) => (
      <div key={i} className="flex items-center gap-4" style={{ animationDelay: `${delay}ms` }}>
        <SkeletonPulse className="h-10 w-10 rounded-full" color="violet" delay={delay} />
        <div className="flex-1 space-y-2">
          <SkeletonShimmer className="h-4 w-48" delay={delay + 50} />
          <SkeletonShimmer className="h-3 w-64" delay={delay + 100} />
        </div>
        <SkeletonPulse className="h-5 w-20 rounded-full" color="green" delay={delay + 150} />
      </div>
    ))}
  </div>
);

// ─── Reusable fee display pill ────────────────────────────────────────────────
const FeePill = ({ icon: Icon, label, amount, currency, color }) => (
  <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${color}`}>
    <Icon className="h-3.5 w-3.5 flex-shrink-0" />
    <span className="text-gray-500">{label}:</span>
    <span>{currency} {Number(amount).toLocaleString()}</span>
  </div>
);

// ─── Reusable fee input row (used in both create + edit dialogs) ──────────────
const FeeInputRow = ({ icon: Icon, iconColor, label, fee, onChange }) => (
  <div className="space-y-2">
    <Label className="flex items-center gap-1.5">
      <Icon className={`h-4 w-4 ${iconColor}`} />
      {label}
    </Label>
    <div className="flex gap-3">
      <Input
        type="number" min={0}
        value={fee.totalAmount}
        onChange={e => onChange({ ...fee, totalAmount: e.target.value })}
        placeholder="e.g., 15000 (0 for free)"
        className="flex-1"
        required
      />
      <Select value={fee.currency} onValueChange={v => onChange({ ...fee, currency: v })}>
        <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="INR">INR</SelectItem>
          <SelectItem value="USD">USD</SelectItem>
          <SelectItem value="EUR">EUR</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
export default function CourseManagement() {
  usePageMeta(PAGE_META.courses);
  const [coursesList, setCoursesList]       = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalCourses: 0, activeCourses: 0,
    avgOnlineFee: 0, avgOfflineFee: 0,
    activeTrainers: 0,
    batchStatistics:   { totalBatches: 0, activeBatches: 0, upcomingBatches: 0, completedBatches: 0 },
    studentStatistics: { totalEnrolled: 0 },
  });
  const [loading, setLoading] = useState(false);
  const [addingCourse,      setAddingCourse]      = useState(false);
  const [updatingCourse,    setUpdatingCourse]    = useState(false);
  const [addingBatch,       setAddingBatch]       = useState(false);
  const [updatingBatch,     setUpdatingBatch]     = useState(false);
  const [deletingBatchId,   setDeletingBatchId]   = useState(null);
  const [removingStudentId, setRemovingStudentId] = useState(null);

  // Dialogs
  const [isAddDialogOpen,       setIsAddDialogOpen]       = useState(false);
  const [isEditDialogOpen,      setIsEditDialogOpen]      = useState(false);
  const [isAddBatchDialogOpen,  setIsAddBatchDialogOpen]  = useState(false);
  const [isEditBatchDialogOpen, setIsEditBatchDialogOpen] = useState(false);

  // Views
  const [viewMode,        setViewMode]        = useState('list');
  const [selectedCourse,  setSelectedCourse]  = useState(null);
  const [courseBatches,   setCourseBatches]   = useState([]);
  const [selectedBatch,   setSelectedBatch]   = useState(null);
  const [batchStudents,   setBatchStudents]   = useState([]);
  const [batchTrainers,   setBatchTrainers]   = useState([]);
  const [batchFeeSummary,  setBatchFeeSummary]  = useState(null);
  const [batchLoading,     setBatchLoading]     = useState(false);
  const [batchModeFilter,    setBatchModeFilter]    = useState('offline');
  const [batchPage,          setBatchPage]          = useState(1);
  const BATCH_PAGE_SIZE = 5;
  const [coursePage,         setCoursePage]         = useState(1);
  const COURSE_PAGE_SIZE = 10;
  const [coursePagination,   setCoursePagination]   = useState({
    currentPage: 1, totalPages: 1, totalCourses: 0, hasNextPage: false, hasPrevPage: false,
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedCourseFilters, setAppliedCourseFilters] = useState({
    search: '', status: 'all', feeMode: '', minFee: '', maxFee: '',
    durationUnit: '', minDuration: '', maxDuration: '', fromDate: '', toDate: '', sort: 'newest',
  });
  const [draftCourseFilters, setDraftCourseFilters] = useState(appliedCourseFilters);

  // Forms
  const [newCourse,    setNewCourse]    = useState(BLANK_COURSE);
  const [editCourse,   setEditCourse]   = useState(null);
  const [newBatch,     setNewBatch]     = useState({
    courseId: '', capacity: '', mode: 'online',
    startDate: '', endDate: '', trainerId: '', trainerRole: 'primary', schedule: [],
  });
  const [editBatch,      setEditBatch]      = useState(null);
  const [scheduleRanges, setScheduleRanges] = useState([{ fromDay: 'Monday', toDay: 'Friday', startTime: '', endTime: '' }]);
  // Schedule ranges used inside the Edit Batch dialog — kept separate from the
  // Create Batch ranges above so the two dialogs never clobber each other's state.
  const [editScheduleRanges, setEditScheduleRanges] = useState([{ fromDay: 'Monday', toDay: 'Friday', startTime: '', endTime: '' }]);

  // Auto-generated batch name + code preview
  const [batchMeta,        setBatchMeta]        = useState({ name: '', code: '' });
  const [batchMetaLoading, setBatchMetaLoading] = useState(false);
  const metaDebounceRef = useRef(null);

  // Trainer schedule preview (for conflict awareness in Create Batch dialog)
  const [trainerSchedule,        setTrainerSchedule]        = useState([]);
  const [trainerScheduleLoading, setTrainerScheduleLoading] = useState(false);

  useEffect(() => { fetchInitialData(coursePage, appliedCourseFilters); }, [coursePage, appliedCourseFilters]);

  // ── Auto-generate batch name + code ─────────────────────────────────────
  const fetchBatchMeta = useCallback(async (courseId, mode, schedRanges, startDate) => {
    // Need at least courseId, mode, a time slot, and start date
    const firstSlot = schedRanges.find(r => r.startTime);
    if (!courseId || !mode || !firstSlot?.startTime || !startDate) {
      setBatchMeta({ name: '', code: '' });
      return;
    }
    setBatchMetaLoading(true);
    try {
      const params = new URLSearchParams({
        courseId,
        mode,
        startTime: firstSlot.startTime,
        startDate,
      });
      const r = await fetch(`${API_BASE_URL}/batches/generate-meta?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) throw new Error('Failed to generate meta');
      const data = await r.json();
      setBatchMeta({ name: data.name, code: data.code });
    } catch {
      setBatchMeta({ name: '', code: '' });
    } finally {
      setBatchMetaLoading(false);
    }
  }, []);

  // Debounce re-fetch whenever relevant inputs change
  useEffect(() => {
    if (!selectedCourse) return;
    clearTimeout(metaDebounceRef.current);
    metaDebounceRef.current = setTimeout(() => {
      fetchBatchMeta(selectedCourse._id, newBatch.mode, scheduleRanges, newBatch.startDate);
    }, 400);
  }, [selectedCourse, newBatch.mode, newBatch.startDate, scheduleRanges, fetchBatchMeta]);

  // ── Data fetching ────────────────────────────────────────────────────────
  const fetchInitialData = async (page = coursePage, filters = appliedCourseFilters) => {
    try {
      setLoading(true);
      const coursesData = await apiService.getCourses(page, COURSE_PAGE_SIZE, filters);
      const courses = coursesData.data || coursesData || [];
      setCoursesList(courses);
      setDashboardStats(coursesData.dashboardStats || {});
      if (coursesData.pagination) setCoursePagination(coursesData.pagination);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCourseFilters = () => {
    setAppliedCourseFilters({ ...draftCourseFilters });
    setCoursePage(1);
    setFilterOpen(false);
  };

  const handleClearCourseFilters = () => {
    const clearedFilters = {
      search: '', status: 'all', feeMode: '', minFee: '', maxFee: '',
      durationUnit: '', minDuration: '', maxDuration: '', fromDate: '', toDate: '', sort: 'newest',
    };
    setDraftCourseFilters(clearedFilters);
    setFilterOpen(false);
    if (Object.keys(clearedFilters).some(key => appliedCourseFilters[key] !== clearedFilters[key])) {
      setAppliedCourseFilters(clearedFilters);
    }
    setCoursePage(currentPage => currentPage === 1 ? currentPage : 1);
  };

  // ── Fee helpers ──────────────────────────────────────────────────────────
  const buildFeesPayload = (course) => {
    const payload = {
      online:  { totalAmount: Number(course.fees.online.totalAmount),  currency: course.fees.online.currency  || 'INR' },
      offline: { totalAmount: Number(course.fees.offline.totalAmount), currency: course.fees.offline.currency || 'INR' },
    };
    // Weekend is optional — only include when a value is entered
    const wkAmt = course.fees.weekend?.totalAmount;
    if (wkAmt !== '' && wkAmt !== null && wkAmt !== undefined) {
      payload.weekend = { totalAmount: Number(wkAmt), currency: course.fees.weekend?.currency || 'INR' };
    }
    return payload;
  };

  const validateFees = (fees) => {
    const onlineAmt  = fees.online?.totalAmount;
    const offlineAmt = fees.offline?.totalAmount;
    const weekendAmt = fees.weekend?.totalAmount;
    if (onlineAmt  === '' || onlineAmt  === null || onlineAmt  === undefined || Number(onlineAmt)  < 0 || isNaN(Number(onlineAmt)))  return 'Online fee must be 0 or greater';
    if (offlineAmt === '' || offlineAmt === null || offlineAmt === undefined || Number(offlineAmt) < 0 || isNaN(Number(offlineAmt))) return 'Offline fee must be 0 or greater';
    if (weekendAmt !== '' && weekendAmt !== null && weekendAmt !== undefined && (Number(weekendAmt) < 0 || isNaN(Number(weekendAmt)))) return 'Weekend fee must be 0 or greater';
    return null;
  };

  // ── Handlers: course ─────────────────────────────────────────────────────
  const handleAddCourse = async (e) => {
    e.preventDefault();
    const feeError = validateFees(newCourse.fees);
    if (feeError) { toast.error(feeError); return; }

    setAddingCourse(true);
    try {
      await apiService.createCourse({
        name:         newCourse.name.trim(),
        description:  newCourse.description.trim(),
        duration:     parseInt(newCourse.duration),
        durationUnit: newCourse.durationUnit,
        fees:         buildFeesPayload(newCourse),
      });
      setNewCourse(BLANK_COURSE);
      setIsAddDialogOpen(false);
      fetchInitialData();
      toast.success('Course created successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to create course');
    } finally {
      setAddingCourse(false);
    }
  };

  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    const feeError = validateFees(editCourse.fees);
    if (feeError) { toast.error(feeError); return; }

    setUpdatingCourse(true);
    try {
      await apiService.updateCourse(editCourse._id, {
        name:         editCourse.name,
        description:  editCourse.description,
        duration:     parseInt(editCourse.duration),
        durationUnit: editCourse.durationUnit || autoUnit(editCourse.duration),
        fees:         buildFeesPayload(editCourse),
        status:       editCourse.status,
      });
      setIsEditDialogOpen(false);
      fetchInitialData();
      if (selectedCourse?._id === editCourse._id) {
        const updated = await apiService.getCourse(editCourse._id);
        setSelectedCourse(updated.data);
      }
      toast.success('Course updated successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to update course');
    } finally {
      setUpdatingCourse(false);
    }
  };

  const handleViewCourse = async (course) => {
    try {
      setLoading(true);
      const data = await apiService.getCourse(course._id);
      // console.log('Course detail fetched:', data);
      // console.log('Course fees:', data.data?.fees);
      setSelectedCourse(data.data);
      setCourseBatches(data.data.batches || []);
      setViewMode('details');
    } catch (err) {
      toast.error(err.message || 'Failed to fetch course details');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedCourse(null);
    setCourseBatches([]);
    setSelectedBatch(null);
  };

  // ── Schedule range helpers ───────────────────────────────────────────────
  const DAYS_ORDER = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
  const DAY_SHORT  = { Monday:'Mon', Tuesday:'Tue', Wednesday:'Wed', Thursday:'Thu', Friday:'Fri', Saturday:'Sat', Sunday:'Sun' };

  const expandRangeToDays = ({ fromDay, toDay, startTime, endTime }) => {
    const fi = DAYS_ORDER.indexOf(fromDay);
    const ti = DAYS_ORDER.indexOf(toDay);
    if (fi === -1 || ti === -1 || fi > ti || !startTime || !endTime) return [];
    return DAYS_ORDER.slice(fi, ti + 1).map(day => ({ day, startTime, endTime }));
  };

  // Reverse of expandRangeToDays: groups a flat schedule array (as stored on
  // a batch document) back into { fromDay, toDay, startTime, endTime } ranges,
  // so an existing batch's schedule can be loaded into the range editor UI.
  const collapseScheduleToRanges = (schedule) => {
    if (!schedule?.length) return [{ fromDay: 'Monday', toDay: 'Friday', startTime: '', endTime: '' }];
    const sorted = [...schedule].sort((a, b) => DAYS_ORDER.indexOf(a.day) - DAYS_ORDER.indexOf(b.day));
    const ranges = [];
    sorted.forEach((s) => {
      const last = ranges[ranges.length - 1];
      const sameTime = last && last.startTime === s.startTime && last.endTime === s.endTime;
      const consecutive = last && DAYS_ORDER.indexOf(s.day) === DAYS_ORDER.indexOf(last.toDay) + 1;
      if (sameTime && consecutive) {
        last.toDay = s.day;
      } else {
        ranges.push({ fromDay: s.day, toDay: s.day, startTime: s.startTime, endTime: s.endTime });
      }
    });
    return ranges;
  };

  const fmt12h = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  // Collapse a flat schedule array into human-readable range strings
  // e.g. [{day:'Monday',startTime:'09:00',endTime:'12:00'}, ...Friday] → "Mon – Fri (9:00 AM – 12:00 PM)"
  const formatSchedule = (schedule) => {
    if (!schedule?.length) return '—';
    const order = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
    const short  = { Monday:'Mon', Tuesday:'Tue', Wednesday:'Wed', Thursday:'Thu', Friday:'Fri', Saturday:'Sat', Sunday:'Sun' };
    // group consecutive days sharing the same time slot
    const groups = [];
    schedule.forEach((s) => {
      const last = groups[groups.length - 1];
      const sameTime = last && last.startTime === s.startTime && last.endTime === s.endTime;
      const consecutive = last && order.indexOf(s.day) === order.indexOf(last.lastDay) + 1;
      if (sameTime && consecutive) {
        last.lastDay = s.day;
      } else {
        groups.push({ firstDay: s.day, lastDay: s.day, startTime: s.startTime, endTime: s.endTime });
      }
    });
    return groups.map(({ firstDay, lastDay, startTime, endTime }) => {
      const dayPart = firstDay === lastDay ? short[firstDay] : `${short[firstDay]} – ${short[lastDay]}`;
      return `${dayPart} (${fmt12h(startTime)} – ${fmt12h(endTime)})`;
    }).join(', ');
  };

  // Create Batch dialog range helpers
  const addScheduleRange    = () => setScheduleRanges([...scheduleRanges, { fromDay: 'Monday', toDay: 'Friday', startTime: '', endTime: '' }]);
  const removeScheduleRange = (i) => setScheduleRanges(scheduleRanges.filter((_, idx) => idx !== i));
  const updateScheduleRange = (i, field, val) => {
    const r = [...scheduleRanges]; r[i] = { ...r[i], [field]: val }; setScheduleRanges(r);
  };

  // Edit Batch dialog range helpers (mirror the create-batch ones above,
  // scoped to their own state so editing a batch never touches the
  // Create Batch dialog's in-progress schedule)
  const addEditScheduleRange    = () => setEditScheduleRanges([...editScheduleRanges, { fromDay: 'Monday', toDay: 'Friday', startTime: '', endTime: '' }]);
  const removeEditScheduleRange = (i) => setEditScheduleRanges(editScheduleRanges.filter((_, idx) => idx !== i));
  const updateEditScheduleRange = (i, field, val) => {
    const r = [...editScheduleRanges]; r[i] = { ...r[i], [field]: val }; setEditScheduleRanges(r);
  };

  // ── Handlers: batch ──────────────────────────────────────────────────────
  const handleAddBatch = async (e) => {
    e.preventDefault();
    const schedule = scheduleRanges.flatMap(expandRangeToDays);
    if (!schedule.length) { toast.error('Please fill in at least one complete schedule range'); return; }
    setAddingBatch(true);
    try {
      await apiService.createBatch({
        courseId: selectedCourse._id,
        startDate: newBatch.startDate, endDate: newBatch.endDate,
        capacity: parseInt(newBatch.capacity), mode: newBatch.mode,
        trainerId: newBatch.trainerId, trainerRole: newBatch.trainerRole, schedule,
      });
      setNewBatch({ courseId: '', capacity: '', mode: 'online', startDate: '', endDate: '', trainerId: '', trainerRole: 'primary', schedule: [] });
      setScheduleRanges([{ fromDay: 'Monday', toDay: 'Friday', startTime: '', endTime: '' }]);
      setBatchMeta({ name: '', code: '' });
      setTrainerSchedule([]);
      setIsAddBatchDialogOpen(false);
      if (selectedCourse) {
        const updated = await apiService.getCourse(selectedCourse._id);
        setCourseBatches(updated.data.batches || []);
      }
      toast.success('Batch created successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to create batch');
    } finally {
      setAddingBatch(false);
    }
  };

  const handleUpdateBatch = async (e) => {
    e.preventDefault();
    const schedule = editScheduleRanges.flatMap(expandRangeToDays);
    if (!schedule.length) { toast.error('Please fill in at least one complete schedule range'); return; }
    setUpdatingBatch(true);
    try {
      await apiService.updateBatch(editBatch._id, {
        startDate: editBatch.startDate, endDate: editBatch.endDate,
        capacity: parseInt(editBatch.capacity), mode: editBatch.mode,
        trainerId: editBatch.trainerId, trainerRole: editBatch.trainerRole,
        schedule, status: editBatch.status,
      });
      setIsEditBatchDialogOpen(false);
      if (selectedCourse) {
        const updated = await apiService.getCourse(selectedCourse._id);
        setCourseBatches(updated.data.batches || []);
      }
      toast.success('Batch updated successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to update batch');
    } finally {
      setUpdatingBatch(false);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Delete this batch?')) return;
    setDeletingBatchId(batchId);
    try {
      const r = await fetch(`${API_BASE_URL}/batches/${batchId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      });
      if (!r.ok) throw new Error(await extractServerError(r, 'Failed to delete batch'));
      setCourseBatches(courseBatches.filter(b => b._id !== batchId));
      toast.success('Batch deleted successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to delete batch');
    } finally {
      setDeletingBatchId(null);
    }
  };

  const handleViewBatchStudents = async (batch) => {
    try {
      setBatchLoading(true);
      const data = await apiService.getBatch(batch._id);
      setSelectedBatch(data.data);
      setBatchStudents(data.data.students || []);
      setBatchTrainers(data.data.trainers || []);
      setBatchFeeSummary(data.data.batchFeeSummary || null);
    } catch (err) {
      toast.error(err.message || 'Failed to fetch batch details');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleRemoveStudent = async (studentId) => {
    if (!window.confirm('Remove this student from the batch?')) return;
    setRemovingStudentId(studentId);
    try {
      const r = await fetch(`${API_BASE_URL}/students/unenroll`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentId, batchId: selectedBatch._id }),
      });
      if (!r.ok) throw new Error(await extractServerError(r, 'Failed to remove student'));
      const updated = await apiService.getBatch(selectedBatch._id);
      setSelectedBatch(updated.data);
      setBatchStudents(updated.data.students || []);
      setBatchFeeSummary(updated.data.batchFeeSummary || null);
      toast.success('Student removed from batch');
    } catch (err) {
      toast.error(err.message || 'Failed to remove student');
    } finally {
      setRemovingStudentId(null);
    }
  };

  // ── Color helpers ────────────────────────────────────────────────────────
  const getStatusColor        = (s) => ({ active: 'bg-green-100 text-green-800', inactive: 'bg-red-100 text-red-800', archived: 'bg-red-100 text-red-800', upcoming: 'bg-blue-100 text-blue-800', completed: 'bg-gray-100 text-gray-800' }[s?.toLowerCase()] || 'bg-gray-100 text-gray-800');
  const getModeColor          = (m) => ({ online: 'bg-blue-100 text-blue-800', offline: 'bg-purple-100 text-purple-800', hybrid: 'bg-orange-100 text-orange-800', weekend: 'bg-teal-100 text-teal-800' }[m?.toLowerCase()] || 'bg-gray-100 text-gray-800');
  const getPaymentStatusColor = (s) => ({ paid: 'bg-green-100 text-green-800', pending: 'bg-yellow-100 text-yellow-800', overdue: 'bg-red-100 text-red-800' }[s?.toLowerCase()] || 'bg-gray-100 text-gray-800');

  const skeletonStyles = `
    @keyframes shimmer      { 0%{transform:translateX(-100%)} 100%{transform:translateX(100%)} }
    @keyframes bubble-pulse { 0%,100%{transform:scale(1);opacity:.8} 50%{transform:scale(1.05);opacity:1} }
    @keyframes wave         { 0%{transform:translateX(-100%)} 100%{transform:translateX(100%)} }
    @keyframes scale-in     { 0%{transform:scale(.95);opacity:0} 100%{transform:scale(1);opacity:1} }
    @keyframes fade-in      { 0%{opacity:0} 100%{opacity:1} }
    @keyframes slide-in     { 0%{transform:translateX(-20px);opacity:0} 100%{transform:translateX(0);opacity:1} }
  `;

  // ════════════════════════════════════════════════════════════════════════
  // LOADING STATE
  // ════════════════════════════════════════════════════════════════════════
  if (loading && viewMode === 'list') {
    return (
      <div className="space-y-6 text-[13px]">
        <style>{skeletonStyles}</style>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-[fade-in_0.5s_ease-out]">
          <div className="space-y-2">
            <SkeletonShimmer className="h-8 w-64" />
            <SkeletonShimmer className="h-4 w-96" delay={100} />
          </div>
          <SkeletonPulse className="h-10 w-40 rounded" color="violet" delay={200} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {['blue','violet','green','orange'].map((c, i) => <StatsCardSkeleton key={c} color={c} delay={i * 100} />)}
        </div>
        <Card className="animate-[scale-in_0.6s_ease-out_forwards] transform scale-95" style={{ animationDelay: '400ms' }}>
          <CardHeader>
            <SkeletonShimmer className="h-6 w-32" delay={500} />
            <SkeletonShimmer className="h-4 w-64" delay={550} />
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  {['Course','Code','Duration','Fee','Batches','Students','Status','Actions'].map(h => (
                    <TableHead key={h} className="text-white font-semibold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[0,100,200,300,400].map((d, i) => <TableRowSkeleton key={i} delay={d + 600} />)}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // COURSE DETAILS VIEW
  // ════════════════════════════════════════════════════════════════════════
  if (viewMode === 'details' && selectedCourse) {
    const sc         = selectedCourse;
    const onlineFee  = sc.fees?.online;
    const offlineFee = sc.fees?.offline;
    const currency   = onlineFee?.currency || offlineFee?.currency || 'INR';

    if (loading) {
      return (
        <div className="space-y-6">
          <style>{skeletonStyles}</style>
          <SkeletonPulse className="h-10 w-40 rounded" color="blue" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['blue','green','orange'].map((c, i) => <StatsCardSkeleton key={c} color={c} delay={i * 100} />)}
          </div>
          <Card><CardContent>{[0,150,300,450].map((d, i) => <BatchCardSkeleton key={i} delay={d + 750} />)}</CardContent></Card>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <style>{skeletonStyles}</style>

        <Button variant="outline" onClick={handleBackToList} className="flex items-center gap-2">
          <ArrowLeftIcon className="h-4 w-4" /> Back to Courses
        </Button>

        {/* Course header — redesigned */}
        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row gap-6">

              {/* Left: identity + meta */}
              <div className="flex-1 min-w-0 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h1 className="text-2xl font-bold text-gray-900 truncate">{sc.name}</h1>
                    {sc.description && (
                      <p className="text-sm text-gray-500 mt-1 leading-relaxed line-clamp-2">{sc.description}</p>
                    )}
                  </div>
                  <Badge className={`${getStatusColor(sc.status)} shrink-0`}>{sc.status}</Badge>
                </div>

                {/* Info chips row */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Code',     value: sc.code },
                    { label: 'Duration', value: fmtDuration(sc.duration, sc.durationUnit) },
                    { label: 'Batches',  value: sc.statistics?.totalBatches  || 0 },
                    { label: 'Students', value: sc.statistics?.totalStudents || 0 },
                    { label: 'Active',   value: sc.statistics?.activeBatches || 0 },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
                      <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">{label}</span>
                      <span className="text-xs font-semibold text-gray-800">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: fee card — reacts to mode filter */}
              <div className="lg:w-56 shrink-0">
                {(() => {
                  const showOnline  = batchModeFilter === 'online'  || batchModeFilter === 'all' || batchModeFilter === 'hybrid';
                  const showOffline = batchModeFilter === 'offline' || batchModeFilter === 'all' || batchModeFilter === 'hybrid';
                  const showWeekend = batchModeFilter === 'weekend' || batchModeFilter === 'all';
                  const weekendFee  = sc.fees?.weekend;
                  return (
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3 h-full">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Fee Structure</p>
                      {showOnline && onlineFee && (
                        <div className="flex items-center justify-between gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5">
                          <div className="flex items-center gap-1.5 text-blue-700">
                            <WifiIcon className="h-4 w-4 shrink-0" />
                            <span className="text-xs font-medium">Online</span>
                          </div>
                          <span className="text-sm font-bold text-blue-800">{onlineFee.currency} {Number(onlineFee.totalAmount).toLocaleString()}</span>
                        </div>
                      )}
                      {showOffline && offlineFee && (
                        <div className="flex items-center justify-between gap-2 bg-purple-50 border border-purple-100 rounded-lg px-3 py-2.5">
                          <div className="flex items-center gap-1.5 text-purple-700">
                            <BuildingOfficeIcon className="h-4 w-4 shrink-0" />
                            <span className="text-xs font-medium">Offline</span>
                          </div>
                          <span className="text-sm font-bold text-purple-800">{offlineFee.currency} {Number(offlineFee.totalAmount).toLocaleString()}</span>
                        </div>
                      )}
                      {showWeekend && weekendFee && (
                        <div className="flex items-center justify-between gap-2 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2.5">
                          <div className="flex items-center gap-1.5 text-teal-700">
                            <span className="text-sm">📅</span>
                            <span className="text-xs font-medium">Weekend</span>
                          </div>
                          <span className="text-sm font-bold text-teal-800">{weekendFee.currency} {Number(weekendFee.totalAmount).toLocaleString()}</span>
                        </div>
                      )}
                      <p className="text-[10px] text-gray-400 leading-snug">Installments (1–4) chosen per student at enrollment.</p>
                    </div>
                  );
                })()}
              </div>

            </div>
          </CardContent>
        </Card>

        {/* Batches */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <CardTitle>Course Batches</CardTitle>
                <CardDescription>Manage batches for this course</CardDescription>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {/* Mode filter pills */}
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  {[
                    { value: 'all',     label: 'All' },
                    { value: 'online',  label: '🌐 Online' },
                    { value: 'offline', label: '🏫 Offline' },
                    { value: 'hybrid',  label: '🔀 Hybrid' },
                    { value: 'weekend', label: '📅 Weekend' },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => { setBatchModeFilter(value); setBatchPage(1); }}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                        batchModeFilter === value
                          ? 'bg-white shadow text-gray-900'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <Button onClick={() => setIsAddBatchDialogOpen(true)} className="bg-gradient-to-r from-blue-500 to-violet-500">
                  <PlusIcon className="h-4 w-4 mr-2" />Add New Batch
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              // Sort: active first (by start date asc), upcoming next (by start date asc), completed last (by end date desc)
              const STATUS_ORDER = { active: 0, upcoming: 1, completed: 2 };
              const filtered = [...courseBatches]
                .filter(b => batchModeFilter === 'all' || b.mode === batchModeFilter)
                .sort((a, b) => {
                  const sa = STATUS_ORDER[a.status] ?? 3;
                  const sb = STATUS_ORDER[b.status] ?? 3;
                  if (sa !== sb) return sa - sb;
                  // Within same status group:
                  if (a.status === 'completed') return new Date(b.endDate) - new Date(a.endDate);
                  return new Date(a.startDate) - new Date(b.startDate);
                });

              const totalBatchPages = Math.max(1, Math.ceil(filtered.length / BATCH_PAGE_SIZE));
              const safeBatchPage   = Math.min(batchPage, totalBatchPages);
              const paginated       = filtered.slice((safeBatchPage - 1) * BATCH_PAGE_SIZE, safeBatchPage * BATCH_PAGE_SIZE);

              return (
                <div className="space-y-4">
                  {/* Info bar */}
                  {filtered.length > 0 && (
                    <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                      <span>
                        Showing {Math.min((safeBatchPage - 1) * BATCH_PAGE_SIZE + 1, filtered.length)}–{Math.min(safeBatchPage * BATCH_PAGE_SIZE, filtered.length)} of {filtered.length} batch{filtered.length !== 1 ? 'es' : ''}
                      </span>
                      <span className="flex gap-2">
                        <span className="text-green-600 font-medium">{filtered.filter(b => b.status === 'active').length} active</span>
                        <span className="text-blue-600 font-medium">{filtered.filter(b => b.status === 'upcoming').length} upcoming</span>
                        <span className="text-gray-400">{filtered.filter(b => b.status === 'completed').length} completed</span>
                      </span>
                    </div>
                  )}

                  {paginated.map((batch) => (
                    <Card key={batch._id} className={`border-2 hover:border-blue-300 transition-colors ${batch.status === 'completed' ? 'opacity-70' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex flex-col lg:flex-row justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <h3 className="text-lg font-bold">{batch.name}</h3>
                              <Badge className={getStatusColor(batch.status)}>{batch.status}</Badge>
                              <Badge className={getModeColor(batch.mode)}>{batch.mode}</Badge>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                              <div>
                                <p className="text-gray-500 text-xs">Timeline</p>
                                <p className="font-medium text-xs">{new Date(batch.startDate).toLocaleDateString()} – {new Date(batch.endDate).toLocaleDateString()}</p>
                              </div>
                              <div>
                                <p className="text-gray-500 text-xs">Capacity</p>
                                <p className="font-medium">{batch.assignedStudents || 0} / {batch.capacity}{batch.assignedStudents >= batch.capacity && <span className="ml-1 text-red-600 font-bold">FULL</span>}</p>
                              </div>
                              <div>
                                <p className="text-gray-500 text-xs mb-1">Schedule</p>
                                <p className="font-medium text-xs text-gray-700">{formatSchedule(batch.schedule)}</p>
                              </div>
                              <div>
                                <p className="text-gray-500 text-xs">Code</p>
                                <p className="font-medium">{batch.code}</p>
                              </div>
                            </div>
                          </div>
                          <div className="flex lg:flex-col gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleViewBatchStudents(batch)} className="flex-1 lg:flex-none">
                              <EyeIcon className="h-4 w-4 mr-2" />View Details
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditBatch(batch);
                                setEditScheduleRanges(collapseScheduleToRanges(batch.schedule));
                                setIsEditBatchDialogOpen(true);
                              }}
                              className="flex-1 lg:flex-none"
                            >
                              <PencilIcon className="h-4 w-4 mr-2" />Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDeleteBatch(batch._id)} disabled={deletingBatchId === batch._id} className="flex-1 lg:flex-none text-red-600 border-red-200 hover:bg-red-50">
                              {deletingBatchId === batch._id ? (
                                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                              ) : (
                                <TrashIcon className="h-4 w-4 mr-2" />
                              )}
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {filtered.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <AcademicCapIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>{courseBatches.length === 0 ? 'No batches yet. Add your first batch to get started!' : `No ${batchModeFilter} batches found.`}</p>
                    </div>
                  )}

                  {/* Batch pagination */}
                  {totalBatchPages > 1 && (
                    <div className="flex justify-center items-center gap-2 pt-2">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => setBatchPage(p => Math.max(1, p - 1))}
                        disabled={safeBatchPage === 1}
                      >Previous</Button>
                      {Array.from({ length: totalBatchPages }, (_, i) => i + 1).map(p => (
                        <Button
                          key={p}
                          variant={p === safeBatchPage ? 'default' : 'outline'}
                          size="sm"
                          className="min-w-[36px]"
                          onClick={() => setBatchPage(p)}
                        >{p}</Button>
                      ))}
                      <Button
                        variant="outline" size="sm"
                        onClick={() => setBatchPage(p => Math.min(totalBatchPages, p + 1))}
                        disabled={safeBatchPage === totalBatchPages}
                      >Next</Button>
                    </div>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Add Batch Dialog */}
        <Dialog open={isAddBatchDialogOpen} onOpenChange={v => { setIsAddBatchDialogOpen(v); if (!v) { setBatchMeta({ name: '', code: '' }); setTrainerSchedule([]); } }}>
          <DialogContent className="sm:max-w-[620px] p-0 overflow-hidden rounded-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <CalendarDaysIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-white">Create New Batch</h2>
                  <p className="text-xs text-emerald-200 mt-0.5">Adding batch for <span className="font-medium">{sc.name}</span></p>
                </div>
              </div>
            </div>

            <form onSubmit={handleAddBatch}>
              <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">

                {/* Identity */}
                <div>
                  <p className="text-[11px] font-semibold text-emerald-600 uppercase tracking-widest mb-3">Batch Identity</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">Capacity <span className="text-red-500">*</span></Label>
                      <Input type="number" value={newBatch.capacity} onChange={e => setNewBatch({ ...newBatch, capacity: e.target.value })} placeholder="e.g., 30" className="h-9 text-sm" required />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">Mode <span className="text-red-500">*</span></Label>
                      <Select value={newBatch.mode} onValueChange={v => setNewBatch({ ...newBatch, mode: v })}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="online">🌐 Online</SelectItem>
                          <SelectItem value="offline">🏫 Offline</SelectItem>
                          <SelectItem value="hybrid">🔀 Hybrid</SelectItem>
                          <SelectItem value="weekend">📅 Weekend</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">Start Date <span className="text-red-500">*</span></Label>
                      <Input type="date" value={newBatch.startDate} onChange={e => setNewBatch({ ...newBatch, startDate: e.target.value })} className="h-9 text-sm" required />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">End Date <span className="text-red-500">*</span></Label>
                      <Input type="date" value={newBatch.endDate} onChange={e => setNewBatch({ ...newBatch, endDate: e.target.value })} className="h-9 text-sm" required />
                    </div>
                  </div>

                  {/* Auto-generated name + code preview */}
                  <div className={`mt-4 rounded-xl border-2 p-3.5 transition-all ${batchMeta.name ? 'border-emerald-200 bg-emerald-50' : 'border-dashed border-gray-200 bg-gray-50'}`}>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                      <span>⚡</span> Auto-generated Batch Identity
                    </p>
                    {batchMetaLoading ? (
                      <div className="flex items-center gap-2 h-8">
                        <div className="h-3 w-3 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
                        <span className="text-xs text-gray-400">Generating…</span>
                      </div>
                    ) : batchMeta.name ? (
                      <div className="space-y-1.5">
                        <div className="flex items-start gap-2">
                          <span className="text-[10px] font-semibold text-gray-500 w-10 pt-0.5 shrink-0">NAME</span>
                          <span className="text-sm font-semibold text-emerald-800 leading-tight">{batchMeta.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold text-gray-500 w-10 shrink-0">CODE</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-xs font-mono font-bold tracking-wider">{batchMeta.code}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">
                        Fill in mode, start date &amp; schedule time to preview the auto-generated name and code.
                      </p>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-100" />

                {/* Schedule */}
                <div>
                  <p className="text-[11px] font-semibold text-teal-600 uppercase tracking-widest mb-1">Weekly Schedule</p>
                  <p className="text-[10px] text-gray-400 mb-3">e.g. Mon – Fri &nbsp;·&nbsp; 9:00 AM – 12:00 PM</p>
                  <div className="space-y-3">
                    {scheduleRanges.map((range, i) => (
                      <div key={i} className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-3">

                        {/* Live preview pill */}
                        {range.fromDay && range.toDay && range.startTime && range.endTime && (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 bg-teal-100 text-teal-700 text-[11px] font-semibold px-2.5 py-1 rounded-full">
                              <CalendarDaysIcon className="h-3 w-3" />
                              {DAY_SHORT[range.fromDay]} – {DAY_SHORT[range.toDay]} &nbsp;·&nbsp; {fmt12h(range.startTime)} – {fmt12h(range.endTime)}
                            </span>
                            <span className="text-[10px] text-gray-400">
                              {expandRangeToDays(range).length} day{expandRangeToDays(range).length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        )}

                        {/* Day range selectors */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Start Day</p>
                            <Select value={range.fromDay} onValueChange={v => updateScheduleRange(i, 'fromDay', v)}>
                              <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {DAYS_ORDER.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">End Day</p>
                            <Select value={range.toDay} onValueChange={v => updateScheduleRange(i, 'toDay', v)}>
                              <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {DAYS_ORDER.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Time range inputs */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Start Time</p>
                            <Input type="time" value={range.startTime} onChange={e => updateScheduleRange(i, 'startTime', e.target.value)} className="h-8 text-xs bg-white" />
                          </div>
                          <div className="space-y-1">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">End Time</p>
                            <Input type="time" value={range.endTime} onChange={e => updateScheduleRange(i, 'endTime', e.target.value)} className="h-8 text-xs bg-white" />
                          </div>
                        </div>

                        {scheduleRanges.length > 1 && (
                          <button type="button" onClick={() => removeScheduleRange(i)} className="flex items-center gap-1 text-[11px] text-red-500 hover:text-red-700 transition-colors">
                            <TrashIcon className="h-3.5 w-3.5" /> Remove this block
                          </button>
                        )}
                      </div>
                    ))}

                    <button type="button" onClick={addScheduleRange} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border-2 border-dashed border-teal-200 text-teal-600 hover:border-teal-400 hover:bg-teal-50 text-xs font-medium transition-colors">
                      <PlusIcon className="h-3.5 w-3.5" /> Add Another Schedule Block
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-100" />

                {/* Trainer */}
                <div>
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">Assign Trainer</p>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700">Primary Trainer <span className="text-gray-400 font-normal">(optional)</span></Label>
                    <TrainerSearchSelect
                      value={newBatch.trainerId}
                      onChange={async (v) => {
                        setNewBatch({ ...newBatch, trainerId: v });
                        setTrainerSchedule([]);
                        if (!v) return;
                        setTrainerScheduleLoading(true);
                        try {
                          const r = await fetch(`${API_BASE_URL}/batches/trainer-schedule?trainerId=${v}`, {
                            headers: { Authorization: `Bearer ${getToken()}` },
                          });
                          const d = await r.json();
                          setTrainerSchedule(d.data || []);
                        } catch {
                          setTrainerSchedule([]);
                        } finally {
                          setTrainerScheduleLoading(false);
                        }
                      }}
                    />
                  </div>

                  {/* Trainer's existing schedule — shown after selecting a trainer */}
                  {newBatch.trainerId && (
                    <div className="mt-3">
                      {trainerScheduleLoading ? (
                        <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                          <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Loading trainer's schedule…
                        </div>
                      ) : trainerSchedule.length === 0 ? (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
                          <span className="text-xs text-emerald-700 font-medium">✓ Trainer has no active batch assignments — no conflicts possible.</span>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 overflow-hidden">
                          <div className="px-3 py-2 bg-amber-100 border-b border-amber-200 flex items-center gap-2">
                            <svg className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide">
                              Trainer's Existing Batches — check for conflicts
                            </p>
                          </div>
                          <div className="divide-y divide-amber-100">
                            {trainerSchedule.map(b => {
                              // Group schedule slots by day range
                              const days = b.schedule.map(s => s.day);
                              const uniqueDays = [...new Set(days)];
                              // Build time ranges: group consecutive days with same time
                              const timeGroups = {};
                              b.schedule.forEach(s => {
                                const key = `${s.startTime}–${s.endTime}`;
                                if (!timeGroups[key]) timeGroups[key] = [];
                                timeGroups[key].push(s.day);
                              });
                              const fmt12 = (t) => {
                                if (!t) return '';
                                const [h, m] = t.split(':').map(Number);
                                const ampm = h >= 12 ? 'PM' : 'AM';
                                const h12 = h % 12 || 12;
                                return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
                              };
                              const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', yyyy: 'numeric' }) : '—';
                              const DAY_ORDER = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
                              const compressDays = (dayArr) => {
                                const sorted = [...dayArr].sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
                                if (sorted.length === 0) return '';
                                if (sorted.length === 1) return sorted[0].slice(0, 3);
                                return `${sorted[0].slice(0, 3)} – ${sorted[sorted.length - 1].slice(0, 3)}`;
                              };

                              return (
                                <div key={b._id} className="px-3 py-2.5 space-y-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-semibold text-gray-800 truncate">{b.name}</span>
                                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                      b.status === 'active' ? 'bg-green-100 text-green-700' :
                                      b.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                                      'bg-gray-100 text-gray-500'
                                    }`}>{b.status}</span>
                                  </div>
                                  {Object.entries(timeGroups).map(([timeKey, dayArr]) => (
                                    <div key={timeKey} className="flex items-center gap-1.5 text-xs text-amber-900">
                                      <span className="font-medium">{compressDays(dayArr)}</span>
                                      <span className="text-amber-400">·</span>
                                      <span>{fmt12(timeKey.split('–')[0])} – {fmt12(timeKey.split('–')[1])}</span>
                                    </div>
                                  ))}
                                  <div className="text-[10px] text-gray-400">
                                    {fmtDate(b.startDate)} → {fmtDate(b.endDate)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                <Button type="button" variant="outline" size="sm" onClick={() => setIsAddBatchDialogOpen(false)} disabled={addingBatch} className="text-sm">Cancel</Button>
                <Button type="submit" size="sm" disabled={addingBatch} className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-sm px-6 gap-2">
                  {addingBatch ? <><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Creating…</> : <><PlusIcon className="h-3.5 w-3.5" /> Create Batch</>}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Batch Dialog */}
        <Dialog
          open={isEditBatchDialogOpen}
          onOpenChange={v => {
            setIsEditBatchDialogOpen(v);
            if (!v) setEditScheduleRanges([{ fromDay: 'Monday', toDay: 'Friday', startTime: '', endTime: '' }]);
          }}
        >
          <DialogContent className="sm:max-w-[620px] p-0 overflow-hidden rounded-2xl">
            {editBatch && (
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center">
                      <PencilIcon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-white">Edit Batch</h2>
                      <p className="text-xs text-violet-200 mt-0.5">Updating <span className="font-medium">{editBatch.name}</span></p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleUpdateBatch}>
                  <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">

                    {/* Basic fields */}
                    <div>
                      <p className="text-[11px] font-semibold text-violet-600 uppercase tracking-widest mb-3">Batch Details</p>

                      {/* Read-only name + code */}
                      <div className="rounded-xl border border-violet-200 bg-violet-50 p-3 mb-4 space-y-1.5">
                        <p className="text-[10px] font-semibold text-violet-400 uppercase tracking-widest mb-1">Auto-generated Identity</p>
                        <div className="flex items-start gap-2">
                          <span className="text-[10px] font-semibold text-gray-500 w-10 pt-0.5 shrink-0">NAME</span>
                          <span className="text-sm font-semibold text-violet-800 leading-tight">{editBatch.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold text-gray-500 w-10 shrink-0">CODE</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-violet-100 text-violet-700 text-xs font-mono font-bold tracking-wider">{editBatch.code}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Capacity</Label>
                          <Input type="number" value={editBatch.capacity} onChange={e => setEditBatch({ ...editBatch, capacity: parseInt(e.target.value) })} className="h-9 text-sm" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Mode</Label>
                          <Select value={editBatch.mode} onValueChange={v => setEditBatch({ ...editBatch, mode: v })}>
                            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="online">🌐 Online</SelectItem>
                              <SelectItem value="offline">🏫 Offline</SelectItem>
                              <SelectItem value="hybrid">🔀 Hybrid</SelectItem>
                              <SelectItem value="weekend">📅 Weekend</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Start Date</Label>
                          <Input type="date" value={editBatch.startDate ? editBatch.startDate.slice(0, 10) : ''} onChange={e => setEditBatch({ ...editBatch, startDate: e.target.value })} className="h-9 text-sm" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">End Date</Label>
                          <Input type="date" value={editBatch.endDate ? editBatch.endDate.slice(0, 10) : ''} onChange={e => setEditBatch({ ...editBatch, endDate: e.target.value })} className="h-9 text-sm" />
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-100" />

                    {/* Status & Trainer */}
                    <div>
                      <p className="text-[11px] font-semibold text-purple-600 uppercase tracking-widest mb-3">Settings</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Status</Label>
                          <Select value={editBatch.status} onValueChange={v => setEditBatch({ ...editBatch, status: v })}>
                            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="upcoming">🔜 Upcoming</SelectItem>
                              <SelectItem value="active">✅ Active</SelectItem>
                              <SelectItem value="completed">🏁 Completed</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-[11px] text-gray-500">You can mark a batch completed before its end date.</p>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Trainer</Label>
                          <TrainerSearchSelect
                            value={editBatch.trainerId || ''}
                            onChange={v => setEditBatch({ ...editBatch, trainerId: v })}
                            placeholder="Select trainer"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-100" />

                    {/* Schedule — editable, mirrors the Create Batch range editor */}
                    <div>
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1">Weekly Schedule</p>
                      <p className="text-[10px] text-gray-400 mb-3">e.g. Mon – Fri &nbsp;·&nbsp; 9:00 AM – 12:00 PM. Changing this will re-check the assigned trainer for conflicts.</p>
                      <div className="space-y-3">
                        {editScheduleRanges.map((range, i) => (
                          <div key={i} className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-3">

                            {/* Live preview pill */}
                            {range.fromDay && range.toDay && range.startTime && range.endTime && (
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center gap-1.5 bg-violet-100 text-violet-700 text-[11px] font-semibold px-2.5 py-1 rounded-full">
                                  <CalendarDaysIcon className="h-3 w-3" />
                                  {DAY_SHORT[range.fromDay]} – {DAY_SHORT[range.toDay]} &nbsp;·&nbsp; {fmt12h(range.startTime)} – {fmt12h(range.endTime)}
                                </span>
                                <span className="text-[10px] text-gray-400">
                                  {expandRangeToDays(range).length} day{expandRangeToDays(range).length !== 1 ? 's' : ''}
                                </span>
                              </div>
                            )}

                            {/* Day range selectors */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Start Day</p>
                                <Select value={range.fromDay} onValueChange={v => updateEditScheduleRange(i, 'fromDay', v)}>
                                  <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {DAYS_ORDER.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1">
                                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">End Day</p>
                                <Select value={range.toDay} onValueChange={v => updateEditScheduleRange(i, 'toDay', v)}>
                                  <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {DAYS_ORDER.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            {/* Time range inputs */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Start Time</p>
                                <Input type="time" value={range.startTime} onChange={e => updateEditScheduleRange(i, 'startTime', e.target.value)} className="h-8 text-xs bg-white" />
                              </div>
                              <div className="space-y-1">
                                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">End Time</p>
                                <Input type="time" value={range.endTime} onChange={e => updateEditScheduleRange(i, 'endTime', e.target.value)} className="h-8 text-xs bg-white" />
                              </div>
                            </div>

                            {editScheduleRanges.length > 1 && (
                              <button type="button" onClick={() => removeEditScheduleRange(i)} className="flex items-center gap-1 text-[11px] text-red-500 hover:text-red-700 transition-colors">
                                <TrashIcon className="h-3.5 w-3.5" /> Remove this block
                              </button>
                            )}
                          </div>
                        ))}

                        <button type="button" onClick={addEditScheduleRange} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border-2 border-dashed border-violet-200 text-violet-600 hover:border-violet-400 hover:bg-violet-50 text-xs font-medium transition-colors">
                          <PlusIcon className="h-3.5 w-3.5" /> Add Another Schedule Block
                        </button>
                      </div>
                    </div>

                  </div>

                  <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                    <Button type="button" variant="outline" size="sm" onClick={() => setIsEditBatchDialogOpen(false)} disabled={updatingBatch} className="text-sm">Cancel</Button>
                    <Button type="submit" size="sm" disabled={updatingBatch} className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-sm px-6 gap-2">
                      {updatingBatch ? <><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Saving…</> : <><PencilIcon className="h-3.5 w-3.5" /> Save Changes</>}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Batch Details Dialog */}
        <Dialog open={selectedBatch !== null} onOpenChange={() => setSelectedBatch(null)}>
          <DialogContent className="sm:max-w-[800px] max-h-[90vh] flex-col overflow-y-auto overflow-x-hidden">
            {selectedBatch && (
              <>
                <DialogHeader>
                  <DialogTitle>Batch: {selectedBatch.name}</DialogTitle>
                  <DialogDescription>
                    {batchStudents.length} / {selectedBatch.capacity} students enrolled
                    {batchFeeSummary && (
                      <span className="ml-2">
                        • Fee collected: {currency} {batchFeeSummary.totalFeeCollected?.toLocaleString()} / {currency} {batchFeeSummary.totalFeeExpected?.toLocaleString()} ({batchFeeSummary.collectionPercentage}%)
                      </span>
                    )}
                  </DialogDescription>
                </DialogHeader>
                <Tabs defaultValue="students" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="students">Students ({batchStudents.length})</TabsTrigger>
                    <TabsTrigger value="trainers">Trainers ({batchTrainers.length})</TabsTrigger>
                  </TabsList>
                  <TabsContent value="students" className="space-y-4">
                    {batchLoading ? <BatchDetailsLoadingSkeleton /> : (
                      <>
                        <p className="text-sm text-gray-500">{selectedBatch.capacity - batchStudents.length} seats available</p>
                        <div className="border rounded-lg">
                          <Table>
                            <TableHeader>
                              <TableRow>{['Student Name','Email','Phone','Payment','Actions'].map(h => <TableHead key={h} className="text-white font-semibold">{h}</TableHead>)}</TableRow>
                            </TableHeader>
                            <TableBody>
                              {batchStudents.map(student => (
                                <TableRow key={student._id}>
                                  <TableCell className="font-medium">{student.name}</TableCell>
                                  <TableCell>{student.email}</TableCell>
                                  <TableCell>{student.phone}</TableCell>
                                  <TableCell>
                                    <div className="space-y-1">
                                      <Badge className={getPaymentStatusColor(student.feeDetails?.feeStatus)}>{student.feeDetails?.feeStatus || 'pending'}</Badge>
                                      <p className="text-xs text-gray-600">{currency} {student.feeDetails?.paidFee?.toLocaleString()} / {currency} {student.feeDetails?.totalFee?.toLocaleString()}</p>
                                      {student.feeDetails?.mode && <Badge className={getModeColor(student.feeDetails.mode)} size="sm">{student.feeDetails.mode}</Badge>}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Button size="sm" variant="outline" onClick={() => handleRemoveStudent(student._id)} disabled={removingStudentId === student._id} className="text-red-600">
                                      {removingStudentId === student._id ? (
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                      ) : (
                                        <TrashIcon className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {batchStudents.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-500">No students enrolled yet</TableCell></TableRow>}
                            </TableBody>
                          </Table>
                        </div>
                      </>
                    )}
                  </TabsContent>
                  <TabsContent value="trainers" className="space-y-3">
                    {batchTrainers.map(trainer => (
                      <Card key={trainer._id}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            {trainer.image && <img src={trainer.image} alt={trainer.name} className="w-16 h-16 rounded-full object-cover" />}
                            <div className="flex-1">
                              <h4 className="font-semibold">{trainer.name}</h4>
                              <p className="text-sm text-gray-500">{trainer.email}</p>
                              <p className="text-xs text-gray-400">{trainer.expertise?.slice(0, 3).join(', ')}</p>
                              <Badge variant="outline" className="mt-1">{trainer.role}</Badge>
                            </div>
                            <div className="text-right text-sm">
                              <p className="font-medium">Rating: {trainer.rating}/5</p>
                              <p className="text-xs text-gray-500">{trainer.experience}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {batchTrainers.length === 0 && <p className="text-center py-8 text-gray-500">No trainers assigned yet</p>}
                  </TabsContent>
                </Tabs>
                <DialogFooter><Button variant="outline" onClick={() => setSelectedBatch(null)}>Close</Button></DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // COURSE LIST VIEW
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6 text-[13px]">
      <style>{skeletonStyles}</style>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Course Management</h1>
          <p className="text-gray-500">Create and manage your institute's courses</p>
        </div>

        {/* Add Course Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white">
              <PlusIcon className="h-4 w-4 mr-2" />Add New Course
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[560px] p-0 overflow-hidden rounded-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <AcademicCapIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-white">Create New Course</h2>
                  <p className="text-xs text-blue-200 mt-0.5">Add a new course to your institute's curriculum</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleAddCourse}>
              <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">

                {/* Basic info */}
                <div>
                  <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-widest mb-3">Basic Information</p>
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">Course Name <span className="text-red-500">*</span></Label>
                      <Input value={newCourse.name} onChange={e => setNewCourse({ ...newCourse, name: e.target.value })} placeholder="e.g., Full Stack Web Development" className="h-9 text-sm" required />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">
                        Duration <span className="text-red-500">*</span>
                      </Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          type="number" min={1}
                          value={newCourse.duration}
                          onChange={e => {
                            const val = e.target.value;
                            setNewCourse({ ...newCourse, duration: val, durationUnit: autoUnit(val) });
                          }}
                          placeholder="e.g., 6"
                          className="h-9 text-sm flex-1"
                          required
                        />
                        <Select
                          value={newCourse.durationUnit}
                          onValueChange={v => setNewCourse({ ...newCourse, durationUnit: v })}
                        >
                          <SelectTrigger className="h-9 w-28 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="months">Months</SelectItem>
                            <SelectItem value="days">Days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">Description <span className="text-gray-400 font-normal">(optional)</span></Label>
                      <Textarea value={newCourse.description} onChange={e => setNewCourse({ ...newCourse, description: e.target.value })} placeholder="Course description and curriculum overview..." rows={3} className="text-sm resize-none" />
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-100" />

                {/* Fee Structure */}
                <div>
                  <p className="text-[11px] font-semibold text-indigo-600 uppercase tracking-widest mb-1">Fee Structure</p>
                  <p className="text-[10px] text-gray-400 mb-3">Students choose installment count (1–4) at enrollment. Set total fees here.</p>
                  <div className="space-y-3">
                    <FeeInputRow icon={WifiIcon} iconColor="text-blue-500" label="Online Fee" fee={newCourse.fees.online} onChange={online => setNewCourse({ ...newCourse, fees: { ...newCourse.fees, online } })} />
                    <FeeInputRow icon={BuildingOfficeIcon} iconColor="text-purple-500" label="Offline Fee" fee={newCourse.fees.offline} onChange={offline => setNewCourse({ ...newCourse, fees: { ...newCourse.fees, offline } })} />
                    <FeeInputRow icon={CalendarDaysIcon} iconColor="text-teal-500" label="Weekend Fee (optional)" fee={newCourse.fees.weekend} onChange={weekend => setNewCourse({ ...newCourse, fees: { ...newCourse.fees, weekend } })} />
                  </div>
                </div>

              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                <Button type="button" variant="outline" size="sm" onClick={() => { setIsAddDialogOpen(false); setNewCourse(BLANK_COURSE); }} disabled={addingCourse} className="text-sm">Cancel</Button>
                <Button type="submit" size="sm" disabled={addingCourse} className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm px-6 gap-2">
                  {addingCourse ? <><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Creating…</> : <><PlusIcon className="h-3.5 w-3.5" /> Create Course</>}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Courses',   value: dashboardStats.totalCourses,                                 icon: AcademicCapIcon,  color: 'bg-blue-100',   iconColor: 'text-blue-600' },
          { label: 'Active Trainers', value: dashboardStats.activeTrainers,                               icon: UserGroupIcon,    color: 'bg-violet-100', iconColor: 'text-violet-600' },
          { label: 'Avg Online Fee',  value: `₹ ${(dashboardStats.avgOnlineFee || 0).toLocaleString()}`, icon: WifiIcon,         color: 'bg-green-100',  iconColor: 'text-green-600' },
          { label: 'Active Courses',  value: dashboardStats.activeCourses,                               icon: CalendarDaysIcon, color: 'bg-orange-100', iconColor: 'text-orange-600' },
        ].map(({ label, value, icon: Icon, color, iconColor }) => (
          <Card key={label} className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{label}</p>
                  <p className="text-2xl font-bold text-gray-900">{value}</p>
                </div>
                <div className={`p-2 ${color} rounded-lg`}><Icon className={`h-6 w-6 ${iconColor}`} /></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Course table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>All Courses</CardTitle>
              <CardDescription>Manage and view all institute courses</CardDescription>
            </div>
            <Dialog open={filterOpen} onOpenChange={(open) => {
              setFilterOpen(open);
              if (open) setDraftCourseFilters({ ...appliedCourseFilters });
            }}>
              <Button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="relative w-fit gap-2 border border-gray-200 bg-white text-sm font-medium text-gray-700 shadow-sm hover:border-blue-300 hover:bg-gray-50"
                variant="outline"
              >
                <FunnelIcon className="h-4 w-4 text-blue-500" />
                Search &amp; Filters
                {(() => {
                  const activeCount = [
                    appliedCourseFilters.search,
                    appliedCourseFilters.status !== 'all',
                    appliedCourseFilters.feeMode,
                    appliedCourseFilters.minFee,
                    appliedCourseFilters.maxFee,
                    appliedCourseFilters.durationUnit,
                    appliedCourseFilters.minDuration,
                    appliedCourseFilters.maxDuration,
                    appliedCourseFilters.fromDate,
                    appliedCourseFilters.toDate,
                    appliedCourseFilters.sort !== 'newest',
                  ].filter(Boolean).length;
                  return activeCount > 0 && (
                    <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                      {activeCount}
                    </span>
                  );
                })()}
              </Button>
              <DialogContent className="max-h-[92vh] max-w-xl gap-0 overflow-hidden rounded-2xl p-0" hideCloseButton>
                <div className="flex items-center justify-between border-b border-gray-100 bg-gradient-to-r from-blue-600 to-violet-600 px-5 py-4">
                  <div className="flex items-center gap-2">
                    <FunnelIcon className="h-5 w-5 text-white" />
                    <DialogTitle className="text-base font-semibold text-white">Search &amp; Filters</DialogTitle>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFilterOpen(false)}
                    aria-label="Close filters"
                    className="text-white/80 transition-colors hover:text-white"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="max-h-[calc(92vh-132px)] space-y-4 overflow-y-auto p-5">
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs font-medium text-gray-600">Search</Label>
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder="Search by course name, code, or description"
                        value={draftCourseFilters.search}
                        onChange={e => setDraftCourseFilters(filters => ({ ...filters, search: e.target.value }))}
                        className="h-9 pl-9 text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      <Label className="text-xs font-medium text-gray-600">Status</Label>
                      <Select value={draftCourseFilters.status} onValueChange={status => setDraftCourseFilters(filters => ({ ...filters, status }))}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Courses</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label className="text-xs font-medium text-gray-600">Fee Mode</Label>
                      <Select value={draftCourseFilters.feeMode || 'all'} onValueChange={feeMode => setDraftCourseFilters(filters => ({ ...filters, feeMode: feeMode === 'all' ? '' : feeMode }))}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Any Fee Mode" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any Fee Mode</SelectItem>
                          <SelectItem value="online">Online</SelectItem>
                          <SelectItem value="offline">Offline</SelectItem>
                          <SelectItem value="weekend">Weekend</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1">
                      <Label className="text-xs font-medium text-gray-600">Minimum Fee</Label>
                      <Input type="number" min="0" placeholder="No minimum" value={draftCourseFilters.minFee} onChange={e => setDraftCourseFilters(filters => ({ ...filters, minFee: e.target.value }))} className="h-9 text-sm" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label className="text-xs font-medium text-gray-600">Maximum Fee</Label>
                      <Input type="number" min="0" placeholder="No maximum" value={draftCourseFilters.maxFee} onChange={e => setDraftCourseFilters(filters => ({ ...filters, maxFee: e.target.value }))} className="h-9 text-sm" />
                    </div>

                    <div className="flex flex-col gap-1">
                      <Label className="text-xs font-medium text-gray-600">Duration Unit</Label>
                      <Select value={draftCourseFilters.durationUnit || 'all'} onValueChange={durationUnit => setDraftCourseFilters(filters => ({ ...filters, durationUnit: durationUnit === 'all' ? '' : durationUnit }))}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Any Duration" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any Duration</SelectItem>
                          <SelectItem value="months">Months</SelectItem>
                          <SelectItem value="days">Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs font-medium text-gray-600">Min Duration</Label>
                        <Input type="number" min="1" placeholder="Min" value={draftCourseFilters.minDuration} onChange={e => setDraftCourseFilters(filters => ({ ...filters, minDuration: e.target.value }))} className="h-9 text-sm" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs font-medium text-gray-600">Max Duration</Label>
                        <Input type="number" min="1" placeholder="Max" value={draftCourseFilters.maxDuration} onChange={e => setDraftCourseFilters(filters => ({ ...filters, maxDuration: e.target.value }))} className="h-9 text-sm" />
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      <Label className="text-xs font-medium text-gray-600">Created From</Label>
                      <Input type="date" value={draftCourseFilters.fromDate} onChange={e => setDraftCourseFilters(filters => ({ ...filters, fromDate: e.target.value }))} className="h-9 text-sm" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label className="text-xs font-medium text-gray-600">Created To</Label>
                      <Input type="date" value={draftCourseFilters.toDate} onChange={e => setDraftCourseFilters(filters => ({ ...filters, toDate: e.target.value }))} className="h-9 text-sm" />
                    </div>

                    <div className="flex flex-col gap-1 sm:col-span-2">
                      <Label className="text-xs font-medium text-gray-600">Sort By</Label>
                      <Select value={draftCourseFilters.sort} onValueChange={sort => setDraftCourseFilters(filters => ({ ...filters, sort }))}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="newest">Newest First</SelectItem>
                          <SelectItem value="oldest">Oldest First</SelectItem>
                          <SelectItem value="name_asc">Name: A to Z</SelectItem>
                          <SelectItem value="name_desc">Name: Z to A</SelectItem>
                          <SelectItem value="duration_asc">Duration: Shortest First</SelectItem>
                          <SelectItem value="duration_desc">Duration: Longest First</SelectItem>
                          <SelectItem value="fee_low">Fee: Low to High</SelectItem>
                          <SelectItem value="fee_high">Fee: High to Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-5 py-4">
                  <Button type="button" size="sm" variant="outline" onClick={handleClearCourseFilters} className="gap-1.5 text-sm">
                    Clear All
                  </Button>
                  <Button type="button" size="sm" onClick={handleApplyCourseFilters} className="bg-blue-600 px-5 text-sm text-white hover:bg-blue-700">
                    Apply Filters
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {['Course','Code','Duration','Fee','Batches','Students','Status','Actions'].map(h => (
                    <TableHead key={h} className="text-white font-semibold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {coursesList.map((course) => {
                    const of = course.fees?.online;
                    const ff = course.fees?.offline;
                    return (
                      <TableRow key={course._id} className="hover:bg-gray-50">
                        <TableCell className="py-3">
                          <div className="flex items-center space-x-3">
                            {course.imageUrl && <img src={course.imageUrl} alt={course.name} className="w-10 h-10 rounded-lg object-cover" onError={e => e.target.style.display = 'none'} />}
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{course.name}</p>
                              <p className="text-xs text-gray-400 max-w-xs truncate">{course.description}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-3"><p className="text-sm font-medium">{course.code}</p></TableCell>
                        <TableCell className="py-3"><Badge variant="outline" className="text-xs">{fmtDuration(course.duration, course.durationUnit)}</Badge></TableCell>

                        <TableCell className="py-3">
                          <div className="space-y-1">
                            {of?.totalAmount ? (
                              <div className="flex items-center gap-1 text-xs">
                                <WifiIcon className="h-3 w-3 text-blue-500 flex-shrink-0" />
                                <span className="text-gray-500">Online:</span>
                                <span className="font-medium">{of.currency || 'INR'} {Number(of.totalAmount).toLocaleString()}</span>
                              </div>
                            ) : null}
                            {ff?.totalAmount ? (
                              <div className="flex items-center gap-1 text-xs">
                                <BuildingOfficeIcon className="h-3 w-3 text-purple-500 flex-shrink-0" />
                                <span className="text-gray-500">Offline:</span>
                                <span className="font-medium">{ff.currency || 'INR'} {Number(ff.totalAmount).toLocaleString()}</span>
                              </div>
                            ) : null}
                            {!of?.totalAmount && !ff?.totalAmount && (
                              <span className="text-xs text-gray-400">No fees configured</span>
                            )}
                          </div>
                        </TableCell>

                        <TableCell className="py-3">
                          <div className="text-sm">
                            <p>Total: {course.totalBatches || 0}</p>
                            <p className="text-xs text-green-600">Active: {course.activeBatches || 0}</p>
                          </div>
                        </TableCell>
                        <TableCell className="py-3"><p className="font-medium text-sm">{course.totalStudents || 0}</p></TableCell>
                        <TableCell className="py-2"><Badge className={getStatusColor(course.status)}>{course.status}</Badge></TableCell>
                        <TableCell className="py-3">
                          <div className="flex space-x-1">
                            <Button size="sm" variant="outline" onClick={() => handleViewCourse(course)} className="h-8 w-8 p-0">
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => {
                              setEditCourse({
                                ...course,
                                fees: {
                                  online:  course.fees?.online  || { totalAmount: '', currency: 'INR' },
                                  offline: course.fees?.offline || { totalAmount: '', currency: 'INR' },
                                  weekend: course.fees?.weekend || { totalAmount: '', currency: 'INR' },
                                },
                              });
                              setIsEditDialogOpen(true);
                            }}>
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                })}
                {coursesList.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center py-12 text-gray-400">No courses match the current search and filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Course pagination — driven by server-side coursePagination */}
          {coursePagination.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t mt-2">
              <p className="text-sm text-gray-500">
                Showing {(coursePagination.currentPage - 1) * COURSE_PAGE_SIZE + 1}–
                {Math.min(coursePagination.currentPage * COURSE_PAGE_SIZE, coursePagination.totalCourses)} of {coursePagination.totalCourses} courses
              </p>
              <div className="flex items-center gap-1">
                <Button
                  size="sm" variant="outline"
                  disabled={!coursePagination.hasPrevPage}
                  onClick={() => setCoursePage(p => p - 1)}
                  className="h-8 px-3 text-xs"
                >
                  Previous
                </Button>
                {Array.from({ length: coursePagination.totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === coursePagination.totalPages || Math.abs(p - coursePagination.currentPage) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '...' ? (
                      <span key={`ellipsis-${i}`} className="px-2 text-gray-400 text-xs">…</span>
                    ) : (
                      <Button
                        key={p} size="sm"
                        variant={p === coursePagination.currentPage ? 'default' : 'outline'}
                        onClick={() => setCoursePage(p)}
                        className="h-8 w-8 p-0 text-xs"
                      >
                        {p}
                      </Button>
                    )
                  )}
                <Button
                  size="sm" variant="outline"
                  disabled={!coursePagination.hasNextPage}
                  onClick={() => setCoursePage(p => p + 1)}
                  className="h-8 px-3 text-xs"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Course Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[560px] p-0 overflow-hidden rounded-2xl">
          {editCourse && (
            <>
              {/* Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-5">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center">
                    <PencilIcon className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-white">Edit Course</h2>
                    <p className="text-xs text-indigo-200 mt-0.5">Updating <span className="font-medium">{editCourse.name}</span></p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleUpdateCourse}>
                <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">

                  {/* Basic info */}
                  <div>
                    <p className="text-[11px] font-semibold text-indigo-600 uppercase tracking-widest mb-3">Basic Information</p>
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Course Name <span className="text-red-500">*</span></Label>
                        <Input value={editCourse.name} onChange={e => setEditCourse({ ...editCourse, name: e.target.value })} className="h-9 text-sm" required />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Course Code</Label>
                        <Input value={editCourse.code} readOnly className="h-9 text-sm bg-gray-50 cursor-not-allowed text-gray-400" />
                        <p className="text-[10px] text-gray-400">Auto-regenerated if you change the name</p>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Description <span className="text-gray-400 font-normal">(optional)</span></Label>
                        <Textarea value={editCourse.description} onChange={e => setEditCourse({ ...editCourse, description: e.target.value })} rows={3} className="text-sm resize-none" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Duration <span className="text-red-500">*</span></Label>
                          <div className="flex gap-2 items-center">
                            <Input
                              type="number" min={1}
                              value={editCourse.duration}
                              onChange={e => {
                                const val = e.target.value;
                                setEditCourse({ ...editCourse, duration: val, durationUnit: autoUnit(val) });
                              }}
                              className="h-9 text-sm flex-1"
                              required
                            />
                            <Select
                              value={editCourse.durationUnit || autoUnit(editCourse.duration)}
                              onValueChange={v => setEditCourse({ ...editCourse, durationUnit: v })}
                            >
                              <SelectTrigger className="h-9 w-28 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="months">Months</SelectItem>
                                <SelectItem value="days">Days</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Status</Label>
                          <Select value={editCourse.status} onValueChange={v => setEditCourse({ ...editCourse, status: v })}>
                            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">✅ Active</SelectItem>
                              <SelectItem value="archived">📦 Archived</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-100" />

                  {/* Fee Structure */}
                  <div>
                    <p className="text-[11px] font-semibold text-violet-600 uppercase tracking-widest mb-1">Fee Structure</p>
                    <p className="text-[10px] text-gray-400 mb-3">Installment counts are chosen per student at enrollment — only update totals here.</p>
                    <div className="space-y-3">
                      <FeeInputRow icon={WifiIcon} iconColor="text-blue-500" label="Online Fee" fee={editCourse.fees.online} onChange={online => setEditCourse({ ...editCourse, fees: { ...editCourse.fees, online } })} />
                      <FeeInputRow icon={BuildingOfficeIcon} iconColor="text-purple-500" label="Offline Fee" fee={editCourse.fees.offline} onChange={offline => setEditCourse({ ...editCourse, fees: { ...editCourse.fees, offline } })} />
                      <FeeInputRow icon={CalendarDaysIcon} iconColor="text-teal-500" label="Weekend Fee (optional)" fee={editCourse.fees.weekend || { totalAmount: '', currency: 'INR' }} onChange={weekend => setEditCourse({ ...editCourse, fees: { ...editCourse.fees, weekend } })} />
                    </div>
                  </div>

                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                  <Button type="button" variant="outline" size="sm" onClick={() => setIsEditDialogOpen(false)} disabled={updatingCourse} className="text-sm">Cancel</Button>
                  <Button type="submit" size="sm" disabled={updatingCourse} className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white text-sm px-6 gap-2">
                    {updatingCourse ? <><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Saving…</> : <><PencilIcon className="h-3.5 w-3.5" /> Save Changes</>}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}